import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y, Autoplay } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/a11y';
import 'swiper/css/autoplay';
import { useEffect, useState } from 'react';
import * as ROUTES from '../../routes/routes';
import { getAbsoluteRouteForCurrentHost } from '../../routes/routes';
import { IInsurerModel } from '../../helpers/models/insurerModel';
import { fetchInsurer } from '../../helpers/insurers';

const InsurersSlider = (props: WithTranslation) => {
    const { i18n, t } = props;
    const [autoPlay, setAutoPlay] = useState<boolean>(true);
    const swiperRef = React.useRef<SwiperType>();

    const onInit = (swiper: SwiperType): void => {
        swiperRef.current = swiper;
        swiper.el.setAttribute('aria-label', t('slider_aria_label', { ns: 'accessibility' }));
        swiper.el.setAttribute('role', 'region');
    };

    const resumeAutoplay = () => {
        if (swiperRef.current) swiperRef.current.autoplay.start();
    };

    const stopAutoplay = () => {
        if (swiperRef.current) swiperRef.current.autoplay.stop();
    };

    const updateSwiperOnLanguageChange = (swiper: SwiperType) => {
        swiperRef.current = swiper;
        i18n.on('languageChanged', () => {
            swiperRef.current?.update();
            swiperRef.current?.navigation.update();
            swiperRef.current?.pagination.update();
        });
    };

    useEffect(() => {
        autoPlay ? resumeAutoplay() : stopAutoplay();
    }, [autoPlay]);

    const [data, setData] = useState<IInsurerModel[]>([]);
    useEffect(() => {
        async function getData() {
            const insurersList = await fetchInsurer(i18n.language);
            if (insurersList) {
                setData(insurersList);
            }
        }
        getData();
    }, [i18n.language]);

    return (
        <div className="insurers-slider-container">
            <h2 className="insurers-slider-container__title header-3">
                {t('insurers_slider_title')}
            </h2>
            {data.length ? (
                <div className="insurers-slider">
                    <div
                        className="swiper-button-prev"
                        aria-label={t('next_slide_message', { ns: 'accessibility' })}
                    ></div>
                    <div
                        className="swiper-button-next"
                        aria-label={t('prev_slide_message', { ns: 'accessibility' })}
                    ></div>
                    <button
                        className="insurers-slider__button"
                        aria-label={
                            autoPlay
                                ? t('stop_animation_aria_label', { ns: 'accessibility' })
                                : t('start_animation_aria_label', { ns: 'accessibility' })
                        }
                        onClick={() => {
                            setAutoPlay(!autoPlay);
                        }}
                    >
                        <span className="visuallyhidden">
                            {autoPlay
                                ? t('stop_animation_aria_label', { ns: 'accessibility' })
                                : t('start_animation_aria_label', { ns: 'accessibility' })}
                        </span>
                        <span className="insurers-slider__button-label">
                            {autoPlay ? '￭' : '▶'}
                        </span>
                    </button>
                    <Swiper
                        modules={[Navigation, Pagination, A11y, Autoplay]}
                        onInit={onInit}
                        onUpdate={updateSwiperOnLanguageChange}
                        observer={true}
                        spaceBetween={50}
                        slidesPerView={1}
                        navigation={{
                            nextEl: '.swiper-button-next',
                            prevEl: '.swiper-button-prev',
                        }}
                        pagination={{
                            clickable: true,
                        }}
                        centeredSlides={false}
                        grabCursor={true}
                        loop={true}
                        autoplay={{
                            delay: 2500,
                        }}
                        a11y={{
                            enabled: true,
                            prevSlideMessage: t('prev_slide_message', { ns: 'accessibility' }),
                            nextSlideMessage: t('next_slide_message', { ns: 'accessibility' }),
                            paginationBulletMessage:
                                t('pagination_bullet_aria_label', { ns: 'accessibility' }) +
                                ' ' +
                                '{{index}}',
                            lastSlideMessage: t('last_slide_message', { ns: 'accessibility' }),
                            slideLabelMessage: '{{index}} / {{slidesLength}}',
                        }}
                        breakpoints={{
                            640: {
                                slidesPerView: 2,
                                spaceBetween: 20,
                            },
                            768: {
                                slidesPerView: 3,
                                spaceBetween: 30,
                            },
                            1024: {
                                slidesPerView: 4,
                                spaceBetween: 40,
                            },
                        }}
                        className="insurers-slider__swiper"
                    >
                        {data.map((data, i) => (
                            <SwiperSlide key={i}>
                                <img
                                    className="insurers-slider__image"
                                    src={data.uriLogo}
                                    alt={data.name}
                                />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            ) : null}
            <div className="insurers-slider__link-wrapper">
                <a
                    className="insurers-slider__link standalone-link--large"
                    target="_blank"
                    href={getAbsoluteRouteForCurrentHost(ROUTES.ROUTE_INSURERS)}
                    rel="noreferrer"
                >
                    {t('insurers_cta_title').addSpace()}
                    <span aria-hidden="true">&gt;</span>
                </a>
            </div>
        </div>
    );
};

export default withTranslation()(InsurersSlider);
